import React, { useCallback, useState } from 'react';
import { Input, Select, Form, InputNumber, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { SubmissionResponseDtoStatusEnum } from 'libs/pinnacle-sdk';
import { getValidationSchema, initialValues } from './form';
import { validateForm } from 'utils/form';
import FormActionButtons from 'components/ui/form-action-buttons';
import { useMutation } from 'react-query';
import { CloseSubmissionValues } from './types';
import { submissionApi } from 'services/apis';
import { useNavigate, useParams, useLocation } from 'react-router';
import { ENavigationPath } from 'constants/path';
import { SortedSelect } from 'components/ui/sorted-select';

const { TextArea } = Input;

const CloseSubmission: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [isFormValid, setIsFormValid] = useState(true);
  const { submissionId } = useParams<{
    submissionId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const stateStatus = location.state?.status;

  const closeSubmissionMutation = useMutation((values: CloseSubmissionValues) =>
    submissionApi.updateSubmissionStatus({
      submissionId: submissionId || '',
      updateSubmissionStatusPayloadDto: {
        status: values.status,
        competitorRateOnLine: values.competitorRateOnLine,
        competitorRateOnExposure: values.competitorRateOnExposure,
        reason: values.reason,
      },
    })
  );

  const handleSubmit = useCallback(() => {
    form.validateFields().then((values) => {
      closeSubmissionMutation.mutate(values, {
        onSuccess: () => {
          message.success(t('closeSubmission.success'));
          navigate(ENavigationPath.DASHBOARD);
        },
      });
    });
  }, [closeSubmissionMutation, form, navigate, t]);

  const handleNavigateBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return (
    <div className="bg-white rounded-lg p-6">
      <h2 className="text-2xl font-medium mb-6">
        {t('closeSubmission.title')}
      </h2>
      <Form
        form={form}
        layout="vertical"
        className="py-3"
        initialValues={{
          ...initialValues,
          status: stateStatus || initialValues.status,
        }}
        onValuesChange={async () => {
          const values = form.getFieldsValue();
          const isValid = await validateForm(
            values,
            form,
            Object.keys(initialValues),
            getValidationSchema(t)
          );
          setIsFormValid(isValid);
        }}
      >
        <Form.Item
          label={t('closeSubmission.submissionStatus')}
          name="status"
          rules={[
            {
              required: true,
              message: t('closeSubmission.statusRequired'),
            },
          ]}
        >
          <SortedSelect
            size="large"
            options={[
              SubmissionResponseDtoStatusEnum.Declined,
              SubmissionResponseDtoStatusEnum.Cancelled,
            ].map((status) => ({
              label: status,
              value: status,
            }))}
          />
        </Form.Item>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            label={t('closeSubmission.competitorRateOnLine')}
            name="competitorRateOnLine"
            rules={[
              {
                type: 'number',
                min: 0,
                max: 100,
                message: t('closeSubmission.rateRangeError'),
              },
            ]}
          >
            <InputNumber
              size="large"
              className="w-full"
              placeholder={t('closeSubmission.competitorRateOnLine')}
              min={0}
              max={100}
              precision={2}
            />
          </Form.Item>
          <Form.Item
            label={t('closeSubmission.competitorRateOnExposure')}
            name="competitorRateOnExposure"
            rules={[
              {
                type: 'number',
                min: 0,
                max: 100,
                message: t('closeSubmission.rateRangeError'),
              },
            ]}
          >
            <InputNumber
              size="large"
              className="w-full"
              placeholder={t('closeSubmission.competitorRateOnExposure')}
              min={0}
              max={100}
              precision={2}
            />
          </Form.Item>
        </div>

        <Form.Item label={t('closeSubmission.enterReason')} name="reason">
          <TextArea
            size="large"
            placeholder={t('closeSubmission.enterReason')}
            rows={4}
          />
        </Form.Item>

        <FormActionButtons
          confirmText={t('closeSubmission.submitAndClose')}
          cancelText={t('general.cancel')}
          isConfirmDisabled={!isFormValid}
          includeCancel={true}
          includeConfirm={true}
          confirmOutlined={false}
          onCancel={handleNavigateBack}
          onConfirm={handleSubmit}
          cancelColor="primary"
          confirmColor="danger"
        />
      </Form>
    </div>
  );
};

export default CloseSubmission;
